import * as React from "react"
import { Col, Row, Visible } from "react-grid-system"
import Subtitle from "../../atoms/typography/Subtitle"
import styled from "styled-components"
import { MediaQuery } from "../../../utils/design-helpers"
import TextInput from "../../molecules/TextInput"
import H3 from "../../atoms/typography/H3"
import { checkEmailValid } from "../../../utils/helpers"
import { useRegisterAccount } from "../../../hooks/useRegisterAccount"
import { theme } from "../../../components/theme"
import RegisterCompleted from "./RegisterCompleted"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import TextButton from "../../atoms/buttons/TextButton"
import { RECAPTCHA_SITE_KEY } from "../../.."
import { Link } from "react-router-dom"
import { ShareIcon } from "../../atoms/icons/ShareIcon"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { executeGoogleRecaptcha, useRecaptcha } from "../../../utils/recaptcha"

const Content = styled.div`
  padding: 2rem 3rem;
  width: 50vw;

  ${MediaQuery.SM} {
    width: 50vw;
  }

  ${MediaQuery.XS} {
    width: 90vw;
    padding: 1rem;
  }
`

const CustomCheckbox = styled.input`
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  margin: 0;
  width: 24px;
  height: 24px;
  border: 3px solid #616161;
  border-radius: 3px;
  transform: translateY(-0.075em);
  display: grid;
  place-content: center;
  cursor: pointer;

  :hover {
    filter: opacity(0.6);
  }

  ::before {
    content: "";
    width: 12px;
    height: 12px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--form-control-color);
    background-color: #616161;
    transform-origin: bottom left;
    border-radius: 1px;
  }

  :checked::before {
    transform: scale(1);
  }
`

interface Props {
  onClickCancelRegister: () => void
  onClose: () => void
}

const RegisterCard: React.FunctionComponent<Props> = ({ onClickCancelRegister, onClose }) => {
  const [regsiterCompleted, setRegisterCompleted] = React.useState(false)

  const [email, setEmail] = React.useState("")
  const [firstName, setFirstName] = React.useState("")
  const [company, setCompany] = React.useState("")
  const [lastName, setLastName] = React.useState("")
  const [password1, setPassword1] = React.useState("")
  const [password2, setPassword2] = React.useState("")
  const [tc, setTc] = React.useState(false)
  const [recaptchaToken, setRecaptchaToken] = React.useState("")
  useRecaptcha()

  const onCompletedRegisterAccount = (data: any) => {
    console.log("data: ", data)

    if (data?.registerAccount?.success) {
      setRegisterCompleted(true)
    }
  }
  const onErrorRegisterAccount = (error: ApolloError) => {
    captureExceptionWithContext(error, "Error in register account")
  }
  const { registerAccount, data, loading } = useRegisterAccount({
    onCompleted: (data: any) => onCompletedRegisterAccount(data),
    onError: onErrorRegisterAccount,
  })
  const onRegisterAccount = async (event: any) => {
    event.preventDefault()
    executeGoogleRecaptcha("register")
      .then((token) => {
        setRecaptchaToken(token)
        // Now you can send the token to your server with form data
        if (
          checkEmailValid(email) &&
          password1.length > 0 &&
          password1 === password2 &&
          tc &&
          token &&
          company.length > 0 &&
          firstName.length > 0 &&
          lastName.length > 0
        ) {
          try {
            registerAccount({
              variables: {
                company: company,
                email: email,
                firstName: firstName,
                lastName: lastName,
                password1: password1,
                password2: password2,
                recaptchaToken: token,
                acceptsTc: tc,
              },
            })
          } catch (error) {
            captureExceptionWithContext(error, "Error in register account")
          }
        } else {
          console.log("fill in form")
        }
      })
      .catch((error) => {
        alert("There was an error with the reCAPTCHA, please try again")
        captureExceptionWithContext(error, "Error in register account")
      })
  }

  if (regsiterCompleted) {
    return <RegisterCompleted email={email} firstName={firstName} lastName={lastName} />
  }

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <Content>
        <HorizontalSpacer spacing={1} />
        <Subtitle textAlign="center">Register new account</Subtitle>
        <HorizontalSpacer spacing={3} />

        <form onSubmit={(e) => onRegisterAccount(e)}>
          <Row justify="between" align="center">
            <Col md={5}>
              <TextInput
                textFieldTitle="Email"
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                name="email"
                required
                tabIndex={1}
              />
            </Col>
            <Col md={5}>
              <Visible xs>
                <HorizontalSpacer spacing={2} />
              </Visible>
              <TextInput
                textFieldTitle="Company"
                name="company"
                onChange={(e) => setCompany(e.target.value)}
                required
                tabIndex={2}
              />
            </Col>
          </Row>

          <HorizontalSpacer spacing={2} />

          <Row justify="between" align="center">
            <Col md={5}>
              <TextInput
                textFieldTitle="First name"
                name="firstName"
                onChange={(e) => setFirstName(e.target.value)}
                required
                tabIndex={3}
              />
            </Col>
            <Col md={5}>
              <Visible xs>
                <HorizontalSpacer spacing={2} />
              </Visible>
              <TextInput
                textFieldTitle="Last name"
                name="lastName"
                onChange={(e) => setLastName(e.target.value)}
                required
                tabIndex={4}
              />
            </Col>
          </Row>

          <HorizontalSpacer spacing={2} />

          <Row justify="between" align="center">
            <Col md={5}>
              <TextInput
                textFieldTitle="Password"
                name="password1"
                onChange={(e) => setPassword1(e.target.value)}
                type="password"
                required
                tabIndex={5}
              />
            </Col>

            <Col md={5}>
              <Visible xs>
                <HorizontalSpacer spacing={2} />
              </Visible>
              <TextInput
                textFieldTitle="Password"
                name="password2"
                onChange={(e) => setPassword2(e.target.value)}
                type="password"
                inputAccepted={password2.length > 0 && password1 === password2}
                required
                tabIndex={6}
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={2} />

          <Row justify="between" align="center">
            <Col md={12} style={{ display: "flex", alignItems: "center" }}>
              <CustomCheckbox type="checkbox" onChange={() => setTc(!tc)} checked={tc} tabIndex={7} />
              <Spacer spacing={1} />
              <Link to="/terms-and-conditions" target="__blank">
                <TextButton title="I accept the terms and conditions" icon={<ShareIcon />} />
              </Link>
            </Col>
            <Col md={5} style={{ display: "flex", alignItems: "center" }}>
              <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY} data-size="invisible"></div>
            </Col>
          </Row>
          <HorizontalSpacer spacing={3} />
          <Row justify="center">
            <PrimaryButton title="Cancel" onClick={onClickCancelRegister} type="button" />
            <Spacer spacing={2} />
            {loading ? (
              <LoadingSpinner />
            ) : (
              <SecondaryButton
                title="Register"
                type="submit"
                disabled={
                  !(
                    checkEmailValid(email) &&
                    password1.length > 0 &&
                    password1 === password2 &&
                    tc &&
                    company.length > 0 &&
                    firstName.length > 0 &&
                    lastName.length > 0
                  )
                }
              />
            )}
          </Row>
        </form>

        {data?.registerAccount?.errors?.length > 0 && (
          <div>
            <HorizontalSpacer spacing={1} />
            {data.registerAccount.errors.map((x: { field: string; messages: Array<string> }, index: number) => (
              <H3 key={index} color={theme.colors.red}>
                {x.messages}
              </H3>
            ))}
          </div>
        )}
        <HorizontalSpacer spacing={1} />
      </Content>
    </div>
  )
}

export default RegisterCard
